import type { ReactElement, MouseEvent } from 'react';
import { useMemo, useState } from 'react';

import {
    CrossIcon,
    FilterIcon,
} from '@bestseller-bit/frontend-community.theme.buying-theme';
import Badge from '@mui/material/Badge';
import Checkbox from '@mui/material/Checkbox';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

interface FilterValue {
    id: string;
    value: boolean;
}

interface Filter {
    id: string;
    label: string;
    onChange: (newValue: boolean) => void;
}

interface FilterGroup {
    id: string;
    header?: string;
    filters: Array<Filter>;
}

interface FilterButtonProps extends IconButtonProps {
    filterGroups: Array<FilterGroup>;
    filterValues: Array<FilterValue>;
    onClearAllFiltersClick: () => void;
}

const FilterButton = ({
    filterValues,
    filterGroups,
    onClearAllFiltersClick,
    ...iconButtonProps
}: FilterButtonProps): ReactElement => {
    const { palette } = useTheme();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const isFiltersShown = Boolean(anchorEl);

    // If every value is false, we disable the clear button
    const isClearAllDisabled = useMemo(
        () =>
            Object.values(filterValues).every(
                (filterValue) => !filterValue.value
            ),
        [filterValues]
    );

    const activeFilterCount = useMemo(
        () =>
            Object.values(filterValues).filter(
                (filterValue) => filterValue.value
            ).length,
        [filterValues]
    );

    const handleButtonClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    return (
        <>
            <IconButton
                id="filter-button"
                aria-controls="filter-menu"
                aria-haspopup="true"
                aria-expanded={isFiltersShown}
                variant="text"
                aria-label="Filters"
                onClick={handleButtonClick}
                {...iconButtonProps}
            >
                <Badge
                    badgeContent={activeFilterCount}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                >
                    <FilterIcon />
                </Badge>
            </IconButton>
            <Menu
                id="filter-menu"
                anchorEl={anchorEl}
                open={isFiltersShown}
                slotProps={{
                    paper: {
                        sx: { minWidth: '212px' }, // Magic number from design
                    },

                    list: {
                        ['aria-labelledby']: 'filter-button',
                    },
                }}
                onClose={handleClose}
            >
                <MenuItem
                    disabled={isClearAllDisabled}
                    sx={{ padding: 3, gap: 2 }}
                    onClick={() => {
                        onClearAllFiltersClick();
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: '0 !important',
                            color: palette.primary.main,
                        }}
                    >
                        <CrossIcon
                            size={16}
                            color="currentColor" // Will inherit the color form the font color
                        />
                    </ListItemIcon>

                    <ListItemText
                        sx={{ display: 'flex' }}
                        primary={
                            <Typography variant="buttons.xsmall">
                                Clear all
                            </Typography>
                        }
                    />
                </MenuItem>

                {filterGroups.map((group) => (
                    <div key={group.id}>
                        {group.header && (
                            <ListSubheader
                                sx={{
                                    paddingInline: 3,
                                    paddingBlock: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    minHeight: '32px', // Magic number from design
                                }}
                            >
                                <Typography
                                    variant="buttons.xsmall"
                                    sx={{
                                        color: alpha(palette.primary.main, 0.5),
                                    }}
                                >
                                    {group.header}
                                </Typography>
                            </ListSubheader>
                        )}

                        {group.filters.map((filter) => {
                            const currentFilterValue = filterValues.find(
                                (filterValue) => filterValue.id === filter.id
                            )?.value;

                            return (
                                <MenuItem
                                    key={filter.id}
                                    sx={{
                                        paddingInline: 3,
                                        paddingBlock: 2.5,
                                    }}
                                    onClick={() => {
                                        filter.onChange(!currentFilterValue);
                                    }}
                                >
                                    <ListItemIcon sx={{ flexShrink: 0 }}>
                                        <Checkbox
                                            size="small"
                                            edge="start"
                                            checked={currentFilterValue}
                                            tabIndex={-1}
                                            sx={{ marginLeft: 0 }}
                                            disableRipple
                                            slotProps={{
                                                input: {
                                                    ['aria-labelledby']:
                                                        filter.id,
                                                },
                                            }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        id={filter.id}
                                        sx={{ display: 'flex' }}
                                        primary={
                                            <Typography variant="captions.default">
                                                {filter.label}
                                            </Typography>
                                        }
                                    />
                                </MenuItem>
                            );
                        })}
                    </div>
                ))}
            </Menu>
        </>
    );
};

export { FilterButton };
export type { FilterButtonProps, FilterGroup, Filter, FilterValue };
